import { readPref, writePref } from '../ui/prefs';

/**
 * Identité anonyme de l'installation (A78) : un UUID v4 aléatoire créé à la
 * première exécution, aucune donnée personnelle. Il n'est **jamais** remis à
 * zéro par « Effacer la sauvegarde » : c'est l'installation, pas la partie.
 */

export const INSTALL_ID_KEY = 'diceordie.telemetry.id';
export const SEQ_KEY = 'diceordie.telemetry.seq';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/** UUID v4 : `crypto.randomUUID()`, sinon `getRandomValues`, sinon `Math.random` (repli maison). */
export function makeUuid(): string {
  try {
    const c = (globalThis as { crypto?: Crypto }).crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    const bytes = new Uint8Array(16);
    if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
    else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  } catch {
    return '00000000-0000-4000-8000-000000000000';
  }
}

export function isUuid(s: unknown): s is string {
  return typeof s === 'string' && UUID_RE.test(s);
}

/** Identifiant d'installation : relu, ou créé et persisté à la première exécution. */
export function getInstallId(): string {
  const stored = readPref(INSTALL_ID_KEY);
  if (isUuid(stored)) return stored;
  const id = makeUuid();
  writePref(INSTALL_ID_KEY, id);
  return id;
}

/**
 * Prochain numéro de séquence de l'installation (croissant, clé de la file et
 * de l'idempotence côté Worker). `floor` : plus grand numéro déjà connu (file),
 * pour ne jamais réutiliser un numéro si la préférence a disparu.
 */
export function nextSeq(floor = 0): number {
  const stored = Number(readPref(SEQ_KEY));
  const base = Math.max(Number.isFinite(stored) ? stored : 0, floor);
  const seq = Math.floor(base) + 1;
  writePref(SEQ_KEY, String(seq));
  return seq;
}
